import type { PurchaseState } from "../PurchaseModal";
import { useEffect, useState } from "react";
import { useTransitionAnimation } from "@/hooks/useTransitionAnimation";
import type { StartPurchaseRet } from "@skillscratch/shared-types";
import { getStoreById, type StoreListing } from "@/helpers/storeListings";
import { centsToPrettyDollars } from "@skillscratch/shared-util";

export function PurchaseStateTag({ purchaseState, id, result }: { purchaseState: PurchaseState, id: string, result: StartPurchaseRet | undefined }) {
  // Use our custom hook for animation
  const { 
    visibleState, 
    getAnimationClass 
  } = useTransitionAnimation<PurchaseState>(purchaseState, 300, [['checkout', 'checkout-failed']]);
  
  return (
    <div className="relative h-16">
      <div 
        className={`
          absolute inset-0 w-full 
          transition-all duration-300 ease-in-out
          ${getAnimationClass()}
        `}
      >
        {getStateContent(visibleState, id, result)}
      </div>
    </div>
  );
}

function getStateContent(state: PurchaseState, id: string, result: StartPurchaseRet | undefined) {
  const numCards = result?.info.cards ?? 0;
  const pricePerCard = result?.info.pricePerCard ?? 0;
  const storeListing = getStoreById(id) as StoreListing;

  const cardText = numCards > 1 ? 'cards' : 'card';
  const eachText = numCards > 1 ? ' each ' : ' ';

  switch (state) {
    case 'options':
      return (
        <div className="bg-gray-50 h-full text-white p-3 flex justify-center items-center border-t border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-400">How many cards? {centsToPrettyDollars(storeListing?.prices[0].price / storeListing?.prices[0].totalCards, 'round')} each</h1>
        </div>
      );
    case 'checkout' as PurchaseState:
      return (
        <div className="bg-gray-50 h-full text-white p-3 flex justify-center items-center border-t border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-400">{numCards} {cardText} for {centsToPrettyDollars(pricePerCard, 'round')}{eachText}= {centsToPrettyDollars(numCards * pricePerCard, 'round')}</h1>
        </div>
      );
    case 'processing':
      return <AnimatedOrderProcessing />;
    case 'success':
      return (
        <div className="bg-emerald-50 h-full text-white p-3 flex justify-center items-center border-t border-b border-emerald-200">
          <h1 className="text-xl font-bold text-emerald-400">Success. Happy Scratching!</h1>
        </div>
      );
  }
}

function AnimatedOrderProcessing() {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prevDots => {
        if (prevDots === '...') return '';
        if (prevDots === '..') return '...';
        if (prevDots === '.') return '..';
        return '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-sky-50 h-full text-white p-3 flex justify-center items-center border-t border-b border-sky-200">
      <h1 className="text-xl font-bold text-sky-300 translate-x-6">
        Loading<span className="inline-block w-12">{dots}</span>
      </h1>
    </div>
  );
}